import { useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { ApiError, post } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";

/**
 * The page an invitation email lands on.
 *
 * The person has no account yet, so this is reachable signed out. The token in
 * the link is the credential: single use, hashed at rest, fourteen days. On
 * success they are sent to sign in rather than signed in here — the accept
 * call never mints a session, so a leaked link cannot become one.
 */
export default function AcceptInvitePage() {
  const { invitationId = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const result = await post<{ ok: boolean; email: string }>(
        `/invitations/${invitationId}/accept`, { token, password, name: name.trim() },
      );
      setDone(result.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>This link is incomplete</Typography>
          <Typography variant="body2" color="text.secondary">
            An invitation link carries a token after the question mark. Open the link
            exactly as it was sent, or ask whoever invited you for a new one.
          </Typography>
          <Button component={RouterLink} to="/login" variant="outlined">Go to sign in</Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>You are in</Typography>
          <Typography variant="body2" color="text.secondary">
            {done} now has a password. Sign in with it to reach your organisation's
            Console.
          </Typography>
          <Button component={RouterLink} to="/login" variant="contained" size="large">
            Sign in
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2.5}>
        <div>
          <Typography variant="h5" fontWeight={600}>Accept your invitation</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the password you will sign in with: at least 12 characters, with an
            upper-case letter, a lower-case letter and a digit.
          </Typography>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          autoComplete="name"
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <TextField
          label="Password again"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />

        <Button type="submit" variant="contained" size="large" disabled={busy || !password}>
          {busy ? "Saving…" : "Set password"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Already have an account? <RouterLink to="/login">Sign in</RouterLink>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}
